<script lang="ts">
	import { browser } from '$app/environment';
	import { page } from '$app/stores';
	import Button from '$lib/components/ui/Button.svelte';
	import { getLanguageFromPath, t } from '$lib/stores/i18n';
	import { onMount } from 'svelte';

	const STORAGE_KEY = 'pdpa_consent';
	const CONSENT_EXPIRY_DAYS = 365; // Consent expires after 1 year

	let showBanner = false;
	let consentGiven = false;
	let consentDate: string | null = null;

	$: lang = getLanguageFromPath($page.url.pathname) || 'en';

	onMount(() => {
		if (browser) {
			checkConsent();
		}
	});

	function checkConsent() {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			try {
				const consentData = JSON.parse(stored);
				consentGiven = consentData.consent === true;
				consentDate = consentData.date;

				// Check if consent has expired (older than CONSENT_EXPIRY_DAYS)
				if (consentDate) {
					const consentDateObj = new Date(consentDate);
					const expiryDate = new Date(consentDateObj);
					expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

					if (new Date() > expiryDate) {
						// Consent expired, show banner again
						showBanner = true;
						consentGiven = false;
					} else {
						showBanner = false;
					}
				} else {
					showBanner = false;
				}
			} catch (e) {
				// Invalid stored data, show banner
				showBanner = true;
			}
		} else {
			// No consent stored, show banner
			showBanner = true;
		}
	}

	function acceptConsent() {
		if (browser) {
			const consentData = {
				consent: true,
				date: new Date().toISOString()
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
			consentGiven = true;
			showBanner = false;
		}
	}

	function rejectConsent() {
		if (browser) {
			const consentData = {
				consent: false,
				date: new Date().toISOString()
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));
			consentGiven = false;
			showBanner = false;
		}
	}

	function getConsentStatus() {
		if (!browser) return null;
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return null;
			}
		}
		return null;
	}

	// Export function to check consent status (can be used by other components)
	export function hasConsent(): boolean {
		if (!browser) return false;
		const status = getConsentStatus();
		return status?.consent === true;
	}
</script>

{#if showBanner && browser}
	<div class="pdpa-banner">
		<div class="pdpa-banner-content">
			<div class="pdpa-text">
				<p>
					{$t('pdpa.banner.message')}
					<a href="/{lang}/policy" class="pdpa-link">{$t('pdpa.banner.policy')}</a>
					{$t('pdpa.banner.and')}
					<a href="/{lang}/termandcondition" class="pdpa-link">{$t('pdpa.banner.terms')}</a>
				</p>
			</div>
			<div class="pdpa-actions">
				<Button
					className="tg-btn-two"
					onClick={(e) => {
						e.preventDefault();
						rejectConsent();
					}}
					isHaveIcon={false}
				>
					{$t('pdpa.banner.reject')}
				</Button>
				<Button
					className="tg-btn-one"
					onClick={(e) => {
						e.preventDefault();
						acceptConsent();
					}}
					isHaveIcon={false}
				>
					{$t('pdpa.banner.accept')}
				</Button>
			</div>
		</div>
	</div>
{/if}

<style>
	.pdpa-banner {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		background: rgba(26, 26, 26, 0.98);
		border-top: 2px solid #4a6cf7;
		padding: 20px;
		z-index: 10000;
		box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
		backdrop-filter: blur(10px);
	}

	.pdpa-banner-content {
		max-width: 1200px;
		margin: 0 auto;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 20px;
		flex-wrap: wrap;
	}

	.pdpa-text {
		flex: 1;
		min-width: 300px;
	}

	.pdpa-text p {
		margin: 0;
		color: #ffffff;
		font-size: 14px;
		line-height: 1.6;
	}

	.pdpa-link {
		color: #4a6cf7;
		text-decoration: underline;
		transition: color 0.3s ease;
	}

	.pdpa-link:hover {
		color: #6b8aff;
	}

	.pdpa-actions {
		display: flex;
		gap: 12px;
		flex-shrink: 0;
		align-items: center;
	}

	.pdpa-actions :global(.tg-btn) {
		height: 50px;
		min-width: 120px;
		margin: 0;
	}

	@media (max-width: 768px) {
		.pdpa-banner-content {
			flex-direction: column;
			align-items: stretch;
		}

		.pdpa-text {
			min-width: 100%;
		}

		.pdpa-actions {
			width: 100%;
		}

		.pdpa-actions :global(.tg-btn) {
			flex: 1;
		}
	}
</style>
